import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://www.captions.ai/pricing";
const HELP_SUBSCRIPTIONS = "https://captions.ai/help/docs/subscriptions";
const CLIPS = "https://captions.ai/features/create-clips";
const CHECKED = "2026-10-08";

export const CAPTIONS_AI: CompetitorRecord = {
  slug: "captions-ai",
  name: "Captions",
  aliases: ["Captions AI", "Captions app", "captions.ai", "Mirage"],
  url: "https://www.captions.ai",
  pricingUrl: PRICING,
  category: "captions",
  positioning:
    "Mobile-first AI video editor for talking-head content with captions, AI Edit and avatars, from the company now branded Mirage.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "0 credits on the pricing table; the help docs say 60 to 200 lifetime credits",
      includes: [
        "1 caption template",
        "Mirage videos up to 15 seconds",
        "Credits are lifetime, not monthly",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "The pricing table shows 0 credits; the help docs describe 60 to 200 lifetime credits.",
    },
    {
      name: "Basic",
      monthly: 9.99,
      metered: "200 credits per month, charged whether or not you export",
      includes: [
        "AI Creator and AI Edit exports carry a watermark",
        "Listed in the help docs, not on the pricing page",
        "Annual price not published",
      ],
      sourceUrl: HELP_SUBSCRIPTIONS,
      checkedAt: CHECKED,
      note: "Help-docs-only figure. Not on the pricing page as of Oct 8, 2026.",
    },
    {
      name: "Max",
      monthly: 24.99,
      metered: "500 credits per month, charged whether or not you export",
      includes: [
        "No watermark",
        "Priced as an iOS plan on the pricing page",
        "One platform per subscription",
        "Annual price not published",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "The pricing page states the listed prices are iOS plans. Annual billing is described as discounted with no figure.",
    },
    {
      name: "Frontier",
      monthly: 69.99,
      metered: "1,400 credits per month, charged whether or not you export",
      includes: ["No watermark", "One platform per subscription", "Annual price not published"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Frontier 2x",
      monthly: 139.99,
      metered: "2,800 credits per month, charged whether or not you export",
      includes: ["No watermark", "One platform per subscription", "Annual price not published"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Frontier 4x",
      monthly: 279.99,
      metered: "5,600 credits per month, charged whether or not you export",
      includes: [
        "Only self-serve tier that can buy credit top-ups",
        "No watermark",
        "Annual price not published",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Enterprise",
      custom: true,
      metered: "Custom credits",
      includes: ["Team seats", "Contact sales"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Max",
  entryPlanIncludes:
    "500 credits a month on one platform, no watermark; Basic at $9.99 is help-docs-only and watermarks AI exports",
  freeTier:
    "Listed as 0 credits on the pricing table; the help docs describe 60 to 200 lifetime credits, 1 caption template and Mirage videos up to 15 seconds",
  trial: "Not stated on the pricing page",
  meteringUnit:
    "Credits per AI action, deducted whether or not the project is exported; a Mirage video costs 8 credits per second",
  exportResolution: "Not stated on the pricing page",
  features: {
    longToShort: { v: "yes", note: "Create Clips, 30+ clips per upload" },
    momentRanking: { v: "yes", note: "Per-clip score" },
    reframing: { v: "yes", note: "Face tracking follows the speaker" },
    maxSource: { v: "text", note: "3 hours or 10 GB per upload" },
    layouts: { v: "n/a", note: "Not enumerated on the features page" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "no", note: "AI video and avatars only" },
    voiceover: { v: "partial", note: "Faceless voiceover use case only" },
    voiceCloning: { v: "n/a", note: "AI twins advertised, voice terms not stated" },
    avatars: { v: "yes", note: "AI avatars and AI twins" },
    timelineEditor: { v: "partial", note: "Chat-based editor and a simple editor" },
    captionStyles: { v: "text", note: "100+ styles" },
    captionLanguages: { v: "text", note: "100+ caption languages, dubbing in 30+" },
    bRoll: { v: "yes", note: "AI Edit adds B-roll" },
    fourK: { v: "n/a", note: "Export caps not stated" },
    scheduling: { v: "no" },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "no", note: "Mirage API is a separate product" },
    webhooks: { v: "n/a", note: "Not stated on captions.ai" },
    web: { v: "n/a", note: "Desktop plan listed; browser access not stated" },
    desktop: { v: "yes", note: "Separate subscription from the iPhone app" },
    mobile: { v: "yes", note: "iOS and Android apps" },
    teamSeats: { v: "partial", note: "Enterprise only" },
    freeTier: { v: "yes", note: "60 to 200 lifetime credits per help docs" },
    trial: { v: "n/a", note: "Not stated on the pricing page" },
    metering: { v: "text", note: "Credits per action, charged without export" },
    rollover: { v: "text", note: "Pricing FAQ says up to 3x; help doc says none" },
    refunds: { v: "text", note: "Not stated on the pricing page" },
    cancelInApp: { v: "n/a", note: "Not stated on the pricing page" },
  },
  billingTerms: {
    refunds: "No refund terms appear on the pricing page as of Oct 8, 2026.",
    cancel:
      "Cancellation steps are not described on the pricing page. Each platform (desktop, iPhone) is a separate subscription.",
    sourceUrl: PRICING,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "they do not give refunds for any of the monthly subscriptions",
      date: "2026-10-07",
      url: "https://www.trustpilot.com/review/captions.ai",
      evidences: "refunds",
    },
    {
      text: "take your credits away even if you didnt use it",
      date: "2026-09-11",
      url: "https://www.trustpilot.com/review/captions.ai",
      evidences: "rollover",
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note:
      "Pricing page fetched directly on Oct 8, 2026; it states the listed prices are iOS plans and publishes no annual figures. The Basic plan ($9.99, 200 credits, watermarked AI exports) and the Free plan's 60 to 200 lifetime credits come from the help docs at captions.ai/help/docs/subscriptions, not the pricing table. The 8 credits per second Mirage video rate is a third-party figure from hooked.so/compare/captions-ai-pricing checked Sep 25, 2026 and should be browser-checked inside the app. A $4.99 Lite plan is Android-only and is left out of the table. Export resolution caps and trial terms were not found on the pricing page.",
  },
  seoPattern: "One post, captions.ai/blog/captions-vs-capcut; no /vs or /compare hub, no page about Crayo",
};
